/**
 * ¿ESTÁ ESTE CHAT ESPERANDO RESPUESTA? — y por qué sustituye al contador de no leídos.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LO QUE MEDÍA `mensajesNoLeidos`, Y POR QUÉ NO ERA LO QUE HACÍA FALTA (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * El contador decía «mensajes entrantes desde la última vez que alguien PULSÓ este
 * chat», y sólo se ponía a cero al abrirlo en el detalle. Medido sobre los 344 chats:
 *
 *   · 193 llevaban insignia
 *   · de ésos, 183 tenían el contador igual al total de entrantes → NADIE los abrió nunca
 *   · 189 de 339 chats con entrantes reales (55,8 %) no los ha abierto nadie jamás
 *
 * O sea que no estaba sucio: estaba midiendo un hecho sobre EL OPERADOR —si pulsó— y no
 * sobre el cliente. En una bandeja atendida por personas eso es lo mismo; aquí la
 * atiende el BOT, y nadie tiene que abrir los chats que el bot resuelve.
 *
 * ⚠️ CONTRASTADO CONTRA «¿alguien contestó al último mensaje del cliente?»:
 *
 *                        sin responder    ya respondido
 *     llevaba insignia          5              184        ← 179 falsos positivos
 *     NO llevaba                9              141        ← 9 clientes INVISIBLES
 *
 * Precisión 2,6 %, cobertura 36 %. No era sólo ruido: **el instrumento no veía lo que
 * importa**. Benjamín llevaba 14 días sin respuesta y sin una sola marca.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LA REGLA, Y DE DÓNDE SALE EL DATO
 * ───────────────────────────────────────────────────────────────────────────────
 * `ultimoRemitente` lo escriben los TRES escritores vivos del encabezado del chat
 * —`messageRouter.js`, `utils/persistirSaliente.js` y `sendWhatsAppTemplate.js`—, con el
 * mismo vocabulario que `remitente`: `cliente | bot | agent`, sin una cuarta forma.
 *
 * Cubrir los tres no es exhaustividad: si un agente responde desde la bandeja y su
 * escritura no actualizara el campo, el chat quedaría marcado «Sin responder» PARA
 * SIEMPRE — el defecto que esto arregla, reintroducido por la puerta de atrás.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LOS PAUSADOS NO SE MARCAN
 * ───────────────────────────────────────────────────────────────────────────────
 * Decisión de producto del 24-sep-2026, con el dato delante: hay **2 chats pausados**,
 * uno de ellos **ya bloqueado**, y se pausaron cuando el bloqueo no existía. No esperan a
 * nadie: marcarlos reintroduciría el ruido que este cambio elimina.
 *
 * ⚠️ `botPaused` MEZCLA DOS INTENCIONES —«no quiero saber de esta persona» y «esto lo
 * llevo yo»— y hoy sólo la primera existe en los datos. El día que alguien pause para
 * atender personalmente habrá que distinguirlas, y la forma **no es inventar un campo**:
 * es que esa pausa produzca además el hecho de `traspaso`, que ya existe, ya tiene su
 * marcador y su alerta. La regla sería «pausado CON traspaso pendiente sí marca; pausado
 * a secas no». Anotado, no implementado: hoy no hay ni un caso.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LA EXCEPCIÓN: LA PAUSA DEL DETECTOR DE BUCLE SÍ SE MARCA (1-oct-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * El detector de bucle bot↔bot (`functions/bot/utils/detectorDeBucle.js`) pausa el bot con
 * `botPausedBy: 'sistema:bucle'` cuando ve dos entrantes seguidos de ≥ 200 caracteres en
 * ≤ 15 s tras nuestras respuestas. Eso NO lo decidió una persona, y puede equivocarse: una
 * persona que pegue dos textos largos muy rápido también corta. Decisión de Rodrigo: ese chat
 * TIENE que seguir apareciendo «Sin responder», con el motivo «posible conversación con otro
 * bot», porque el coste de un falso positivo es un cliente real ignorado sin ninguna señal.
 *
 * Se distingue por `botPausedBy` y NO por `pausaAutomatica`: ese campo es el registro de que
 * pasó y SE QUEDA al reanudar, mientras que el botón de pausa escribe `botPausedBy: null` al
 * reanudar y el uid de la persona al volver a pausar. Así una pausa humana posterior vuelve a
 * apagar la insignia, que es la regla de arriba.
 *
 * `PAUSA_POR_BUCLE` tiene que ser el mismo literal que `PAUSADO_POR` del detector:
 * `functions/__tests__/esperaDeRespuestaBucle.jest.test.js` los compara.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL TOPE DEL PLAN (R4, 1-oct-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * Al llegar al tope de mensajes, el bot no contesta. El chat NO se pausa, así que ya sale «Sin
 * responder»; lo que faltaba es el motivo. El trigger escribe `topeDelPlan { en, codigo }`, y la
 * insignia dice «tope del plan» mientras esa marca sea igual o posterior a `lastTs` (que el router
 * escribe con cada mensaje): si después entra otro y el bot contesta, deja de esperar; si lo que
 * lo calla es otra cosa, la marca ya es vieja y el motivo desaparece. Sólo con el código de
 * mensajes: la misma rama recibe los bloqueos por pago, y eso no es el tope del plan.
 */

/** Los tres valores que escriben los tres escritores. Ni uno más. */
export const REMITENTES = Object.freeze(['cliente', 'bot', 'agent']);

/** El `botPausedBy` que escribe el detector de bucle. Mismo literal que su `PAUSADO_POR`. */
export const PAUSA_POR_BUCLE = 'sistema:bucle';

/** ¿Está pausado por el detector de bucle, y no por una persona? */
export function esPausaPorBucle(chat) {
  return !!chat && chat.botPaused === true && chat.botPausedBy === PAUSA_POR_BUCLE;
}

/** El `botPausedBy` del R1 (b): el otro lado es una cuenta de negocio de PUX. Mismo literal que `cuentaPux.PAUSADO_POR`. */
export const PAUSA_POR_CUENTA_PUX = 'sistema:cuenta-pux';

/** ¿Pausado porque el otro lado es otra cuenta de PUX (R1 (b)), y no por una persona? */
export function esPausaPorCuentaPux(chat) {
  return !!chat && chat.botPaused === true && chat.botPausedBy === PAUSA_POR_CUENTA_PUX;
}
/**
 * El `botPausedBy` de coexistence (C3, 27-oct-2026): el titular contestó desde su app WhatsApp
 * Business y el bot calló en ese chat. Mismo literal que `PAUSADO_POR` de
 * `functions/integrations/coexistence/pausaPorEcho.js` (`esperaDeRespuestaAppBusiness.jest.test.js`).
 * Decisión de Rodrigo (20-oct-2026): esa pausa sólo se reanuda a mano y el chat SÍ cuenta en
 * «Sin responder»: si el cliente vuelve a escribir, alguien tiene que verlo.
 */
export const PAUSA_POR_APP_BUSINESS = 'app_business';
/**
 * La clave de i18n del banner amarillo del detalle (C11, 28-oct-2026; backlog del 27-oct): una por quién pausó.
 * Una decisión con más de dos ramas, fuera del componente (d74). `detail.bot.<clave>` en es/en/pt.
 */
export function claveDelBannerDePausa(chat) {
  if (esPausaPorAppBusiness(chat)) return 'detail.bot.pausedBannerAppBusiness';
  if (esPausaPorBucle(chat)) return 'detail.bot.pausedBannerBucle';
  if (esPausaPorCuentaPux(chat)) return 'detail.bot.pausedBannerCuentaPux';
  return 'detail.bot.pausedBanner';
}

/** ¿Pausado porque el titular atendió desde su app WhatsApp Business, y no por una persona desde el inbox? */
export function esPausaPorAppBusiness(chat) {
  return !!chat && chat.botPaused === true && chat.botPausedBy === PAUSA_POR_APP_BUSINESS;
}

/**
 * ¿Espera este chat una respuesta?
 *
 * @param {object} chat - el documento del chat
 * @returns {boolean}
 */
export function esperandoRespuesta(chat) {
  if (!chat) return false;
  // Un chat pausado no espera a nadie (ver la cabecera), SALVO si lo pausó el detector de bucle,
  // el R1 (b) (otra cuenta de negocio de PUX) o coexistence (el titular contestó desde su app
  // WhatsApp Business): ninguna de las tres la decidió una persona DESDE EL INBOX, que es lo que
  // la regla del 24-sep protege.
  if (chat.botPaused === true && !esPausaPorBucle(chat) && !esPausaPorCuentaPux(chat) && !esPausaPorAppBusiness(chat)) return false;
  // ⚠️ Comparación EXACTA contra 'cliente', no `!== 'bot'`: un chat sin el campo —los
  // anteriores al backfill— daría `true` con la forma negada y marcaría los 345 de golpe.
  return chat.ultimoRemitente === 'cliente';
}

/** El código que escribe el trigger con el tope de mensajes (`planValidation.canSendMessage`). */
export const CODIGO_TOPE_MENSAJES = 'MESSAGES_LIMIT_REACHED';

/** ¿La marca del tope del plan es la más reciente del chat? (ver la cabecera) */
export function esTopeDelPlan(chat) {
  const m = chat && chat.topeDelPlan;
  if (!m || m.codigo !== CODIGO_TOPE_MENSAJES) return false;
  const en = msDeValor(m.en);
  const ultimo = instanteDeEspera(chat);
  return Number.isFinite(en) && (!Number.isFinite(ultimo) || en >= ultimo);
}

/**
 * Por qué espera, cuando hay algo que decir: `'posible-bot'` si lo pausó el detector de bucle,
 * `'cuenta-pux'` si el otro lado es otra cuenta de PUX, `'app-business'` si el titular atendió
 * desde su app WhatsApp Business (coexistence), `'tope-plan'` si el bot se calló por el tope de
 * mensajes; `null` en cualquier otro caso. Los tres primeros se excluyen entre sí (un solo
 * `botPausedBy`). La tarjeta lo traduce.
 */
export function motivoDeEspera(chat) {
  if (!esperandoRespuesta(chat)) return null;
  if (esPausaPorBucle(chat)) return 'posible-bot';
  if (esPausaPorCuentaPux(chat)) return 'cuenta-pux';
  if (esPausaPorAppBusiness(chat)) return 'app-business';
  if (esTopeDelPlan(chat)) return 'tope-plan';
  return null;
}

/** Milisegundos de un valor de fecha, tolerante a las cuatro formas. */
function msDeValor(v) {
  if (!v) return NaN;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const p = Date.parse(v);
  return Number.isFinite(p) ? p : NaN;
}

/** Milisegundos del último mensaje, tolerante a las cuatro formas. */
export function instanteDeEspera(chat) {
  return msDeValor(chat?.lastTs ?? chat?.actualizadoEn);
}

/**
 * Días completos esperando, o NaN.
 *
 * Días y no un contador de mensajes: «3 sin leer» no es accionable y «14 días» sí — es
 * literalmente el caso que destapó esto. Con `ultimoRemitente === 'cliente'`, `lastTs` ES
 * el instante del último entrante, así que la antigüedad sale sin ningún campo extra.
 */
export function diasEsperando(chat, ahora = Date.now()) {
  if (!esperandoRespuesta(chat)) return NaN;
  const t = instanteDeEspera(chat);
  if (!Number.isFinite(t)) return NaN;
  return Math.max(0, Math.floor((ahora - t) / 86400000));
}
